/**
 * Status Badge (Spoor 2, Verlof MVP)
 *
 * Extracted from `MijnVerlofPage.tsx` (Story 1.2) so the goedkeuringslijst
 * (Story 3.1, V4) and any future verlof screen share one status→label/style
 * mapping instead of drifting copies (AD-1: `request_status` labels are the
 * besluit-status, never mixed with `sync_status` terms — see
 * `SyncStatusChip.tsx`, Story 3.3, for the separate sync-status vocabulary).
 *
 * Story 2.7 (AC-6, AC-7): "vervallen" is geen eigen `request_status`-waarde
 * (AD-10 -- de rij blijft `goedgekeurd`, alleen `hours` gaat naar 0), dus dit
 * component leidt het badge-uiterlijk af uit de combinatie status+uren in
 * plaats van uit een vijfde status. Zo blijft de aanvraag zelf bestaan
 * (AC-6) én blijft "vervallen" visueel duidelijk onderscheiden van
 * "ingetrokken" (AC-7) -- dat is nooit hetzelfde: ingetrokken verlof is nooit
 * goedgekeurd geweest, vervallen verlof was dat wél en is achteraf naar nul
 * gecorrigeerd. Het volledige badge-overzicht (overige statussen herzien)
 * hoort bij Story 2.10; hier alleen de "Vervallen"-variant.
 */

import type { LeaveDisplayStatus, RequestStatus } from '@functieloonhuis/shared';

const STATUS_LABELS: Record<RequestStatus, string> = {
  in_afwachting: 'In afwachting',
  goedgekeurd: 'Goedgekeurd',
  afgewezen: 'Afgewezen',
  ingetrokken: 'Ingetrokken',
};

// XXtract-palet (bindend, FR-23): teal/groen/rood/grijs — zie CLAUDE.md.
const STATUS_STYLES: Record<RequestStatus, string> = {
  in_afwachting: 'bg-[#54949E]/10 text-[#54949E] border-[#54949E]/30',
  goedgekeurd: 'bg-[#B7D945]/15 text-[#5C7A00] border-[#B7D945]/40',
  afgewezen: 'bg-[#D64545]/10 text-[#D64545] border-[#D64545]/30',
  ingetrokken: 'bg-gray-100 text-gray-500 border-gray-300',
};

// Story 2.7 (AC-7): grijs zoals "ingetrokken" (het is ook een eindstatus,
// geen actieve aanwezigheid meer), maar met een eigen label en een
// doorgestreepte stijl zodat de twee nooit voor elkaar aangezien worden.
const VERVALLEN_LABEL = 'Vervallen';
const VERVALLEN_STYLE =
  'bg-gray-100 text-gray-500 border-gray-300 line-through decoration-gray-400';

/**
 * Story 2.10 (AC-3, UX C3): de vijf badges voor GOEDGEKEURD verlof, met de
 * exacte kleurwaarden uit het UX-ontwerp. `LeaveDisplayStatus` is zelf al de
 * letterlijke badgetekst (UX-DR14), dus er is bewust geen tweede
 * label-tabel die uit de pas kan gaan lopen — alleen de stijl staat hier.
 *
 * *Vervallen* houdt de doorhaling uit story 2.7, maar de doorhaling is nooit
 * het enige signaal: het woord staat er (AC-8, NFR-2).
 */
const DISPLAY_STATUS_STYLES: Record<LeaveDisplayStatus, string> = {
  'Uren bevestigd': 'bg-[#F3F7E8] text-[#5C7A00] border-[#5C7A00]/30',
  'Wijziging in behandeling': 'bg-[#EEF2F6] text-[#2F5A7A] border-[#2F5A7A]/30',
  'Wijziging afgewezen': 'bg-[#FDF0F0] text-[#D64545] border-[#D64545]/30',
  'Niet bevestigd': 'bg-[#F1F5F9] text-[#64748B] border-[#64748B]/30',
  Vervallen: 'bg-[#F1F5F9] text-[#64748B] border-[#64748B]/30',
};

export function StatusBadge({
  status,
  hours,
  displayStatus,
}: {
  status: RequestStatus;
  hours?: number;
  /**
   * Story 2.10: een van de vijf badges uit C3, afgeleid op de server. Alleen
   * gevuld voor goedgekeurd verlof; `null`/afwezig laat de bestaande badge
   * staan (AC-4) -- verlof dat nog in afwachting, afgewezen of ingetrokken is
   * verandert hier dus niets.
   */
  displayStatus?: LeaveDisplayStatus | null;
}) {
  // AD-10: geen nieuwe status-waarde -- "vervallen" is `goedgekeurd` met
  // `hours === 0`. `hours` is optioneel zodat bestaande aanroepen zonder
  // uren-informatie (bv. de goedkeuringslijst, waar dit nog niet voorkomt)
  // gewoon het normale "Goedgekeurd"-label blijven tonen.
  const isVervallen = status === 'goedgekeurd' && hours === 0;

  // AC-4: de vijf badges gelden UITSLUITEND voor goedgekeurd verlof. Zou een
  // aanroeper er per ongeluk een meegeven bij een afgewezen aanvraag, dan
  // wordt hij hier alsnog genegeerd -- *wijziging afgewezen* mag nooit op een
  // afgewezen aanvráág belanden.
  const nieuweBadge = status === 'goedgekeurd' ? (displayStatus ?? null) : null;

  if (nieuweBadge) {
    return (
      <span
        data-testid="status-badge"
        className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium border ${DISPLAY_STATUS_STYLES[nieuweBadge]}`}
      >
        {nieuweBadge}
      </span>
    );
  }

  return (
    <span
      data-testid="status-badge"
      className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium border ${
        isVervallen ? VERVALLEN_STYLE : STATUS_STYLES[status]
      }`}
    >
      {isVervallen ? VERVALLEN_LABEL : STATUS_LABELS[status]}
    </span>
  );
}
